import { type CSSProperties, type FormEvent, useState } from 'react';
import { apiJson } from '../../lib/api';

const petals = Array.from({ length: 34 }, (_, index) => ({
  left: `${(index * 29) % 100}vw`,
  x: `${((index * 53) % 240) - 120}px`,
  duration: `${9 + (index % 13)}s`,
  delay: `${-(index % 20)}s`,
  rotation: `rotate(${(index * 47) % 360}deg)`
}));

type ContactResponse = {
  ok: boolean;
};

export function HomePage() {
  const [status, setStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function submitRoadTestRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const name = String(formData.get('name') ?? '');
    const email = String(formData.get('email') ?? '');
    const message = String(formData.get('message') ?? '');

    setIsSubmitting(true);
    setStatus('');

    try {
      await apiJson<ContactResponse>('/api/contact', {
        method: 'POST',
        body: JSON.stringify({ name, email, message })
      });
      form.reset();
      setStatus('Road-test request sent.');
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Unable to send request.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="kyoreido-page">
      <div className="grain" />
      <nav className="nav">
        <a className="brand" href="#top" aria-label="Kyoreido Chronicles home">
          <div className="seal">虚鈴道</div>
          <div className="wordmark">
            <strong>Kyoreido Chronicles</strong>
            <span>Legend of Mugen 夢幻</span>
          </div>
        </a>
        <div className="navlinks">
          <a href="#path">The Path</a>
          <a href="#world">World</a>
          <a href="#summon">Wishlist</a>
          <a href="/admin">Admin</a>
        </div>
      </nav>

      <main id="top">
        <header className="hero">
          <div className="sun" />
          <div className="mist" />
          <div className="mountains" />
          <div className="petals" aria-hidden="true">
            {petals.map((petal, index) => (
              <i
                className="petal"
                key={index}
                style={
                  {
                    left: petal.left,
                    '--x': petal.x,
                    animationDuration: petal.duration,
                    animationDelay: petal.delay,
                    transform: petal.rotation
                  } as CSSProperties
                }
              />
            ))}
          </div>

          <div className="hero-card">
            <div>
              <p className="eyebrow reveal-up in">A wandering folklore adventure</p>
              <h1 className="reveal-up in">
                Legend{' '}
                <br />
                of Mugen
              </h1>
              <p className="subtitle reveal-up in">
                Walk the old roads as Mugen, a komuso monk moving through a living Japan of shrine spirits, mountain
                weather, strange bargains, and stories that remember your choices.
              </p>
              <div className="cta-row reveal-up in">
                <a className="btn primary" href="#summon">
                  Enter the Road
                </a>
                <a className="btn" href="#path">
                  See the Game
                </a>
              </div>
            </div>
            <div className="artifact" aria-hidden="true">
              <div className="torii">
                <div className="ring" />
                <div className="kanji">山</div>
                <div className="gate">
                  <span className="top" />
                  <span className="beam" />
                  <span className="left" />
                  <span className="right" />
                  <span className="center" />
                </div>
              </div>
            </div>
          </div>
        </header>

        <section id="path">
          <div className="wrap">
            <div className="section-title reveal-up in">
              <h2>The road listens.</h2>
              <p>
                Not a menu-heavy game page. This is a mood-forward landing page shaped around movement, omen, ritual,
                and exploration.
              </p>
            </div>
            <div className="features">
              <article className="feature reveal-up in">
                <b>道</b>
                <h3>Living Roads</h3>
                <p>
                  Travel between shrines, villages, mountain passes, tea houses, and forgotten places. Each route carries
                  weather, rumor, danger, and memory.
                </p>
              </article>
              <article className="feature reveal-up in">
                <b>鈴</b>
                <h3>Ritual Presence</h3>
                <p>
                  Play as Mugen with stillness, breath, ki, and attention. The world reacts to posture, offerings, songs,
                  and silence.
                </p>
              </article>
              <article className="feature reveal-up in">
                <b>怪</b>
                <h3>Folklore Encounters</h3>
                <p>
                  Meet miko, kami, thieves, yokai, wandering monks, and villagers with old debts. Speak carefully; names
                  matter.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section id="world" className="world">
          <div className="wrap">
            <div className="section-title reveal-up in">
              <h2>
                A painted world{' '}
                <br />
                with teeth.
              </h2>
              <p>
                Soft parchment, red lacquer, worn gold, dusk blue, and quiet menace. This page keeps the UI DNA without
                copying the in-game interface.
              </p>
            </div>
            <div className="mapline">
              <article className="place reveal-up in">
                <h3>Yamamiya</h3>
                <p>A shrine at the top of the world. The cedars stand older than any name.</p>
                <span className="glyph">山</span>
              </article>
              <article className="place reveal-up in">
                <h3>Toge-no-Chaya</h3>
                <p>A road tea house where travelers trade coin, warnings, and lies.</p>
                <span className="glyph">茶</span>
              </article>
              <article className="place reveal-up in">
                <h3>Kawaraban</h3>
                <p>Broadsheets carry rumor faster than horses and less cleanly than rumor.</p>
                <span className="glyph">瓦</span>
              </article>
              <article className="place reveal-up in">
                <h3>The Mist Road</h3>
                <p>Some roads go where maps refuse to follow. Some come back changed.</p>
                <span className="glyph">霧</span>
              </article>
            </div>
          </div>
        </section>

        <section>
          <div className="wrap trailer">
            <div className="quote reveal-up in">"The mountain knows your true name. It will use it only once."</div>
            <div className="oracle reveal-up in">
              <div className="oracle-log">
                <div className="line">The current is heavy with silt today; step where I place my foot.</div>
                <div className="line">The morning mist clings to the joints like old grief.</div>
                <div className="line">A bell rings where no temple stands.</div>
                <div className="line">At dusk, the shrine gate opens inward.</div>
              </div>
            </div>
          </div>
        </section>

        <section id="summon" className="final">
          <div className="wrap reveal-up in">
            <h2>
              Pack light.{' '}
              <br />
              Listen first.
            </h2>
            <p>
              Kyoreido Chronicles: Legend of Mugen is coming. Follow development, join the first road-test, and receive
              the opening travelogue.
            </p>
            <div className="cta-row">
              <a
                className="btn primary"
                href="mailto:hello@kyoreido.com?subject=Kyoreido%20Chronicles%20Road-Test"
              >
                Join the Road-Test
              </a>
              <a className="btn" href="#top">
                Return to Shrine
              </a>
            </div>
            <form className="road-test-form" onSubmit={submitRoadTestRequest}>
              <label>
                Name
                <input name="name" autoComplete="name" required />
              </label>
              <label>
                Email
                <input name="email" type="email" autoComplete="email" required />
              </label>
              <label>
                Message
                <textarea name="message" required defaultValue="I want to join the first road-test." />
              </label>
              <button className="btn primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Road-Test Request'}
              </button>
              {status ? <p className="form-status">{status}</p> : null}
            </form>
          </div>
        </section>
      </main>
    </div>
  );
}
